import { createLogic } from 'redux-logic';

import {
  types as customerTypes,
  actions as customerActions,
} from '../ducks/customer.js';
import { types as appTypes } from '../ducks/app.js';
import customerApi from '../api/customer.js';
import preferencesApi from '../api/preferences.js';
import { getCurrentOutage, inOutage } from '../helpers/outageHelpers';
import { hasRole } from '../helpers/authHelpers';
import constants from '../constants';
import { get } from 'lodash-es';

function generateBaseConfig(type) {
  return {
    type: type,
    //debounce: 2000,
    latest: true,
    cancelType: `${type}_ERROR`,
    processOptions: {
      dispatchReturn: true,
      successType: `${type}_SUCCESS`,
      failType: `${type}_ERROR`,
    },
  };
}

const getTenancy = (error, dispatch, done, action = null) => {
  if (error) {
    dispatch({
      type: customerTypes.GET_TENANCY_ERROR,
      payload: { fatal: true },
    });
    done();
  } else {
    customerApi
      .getTenancy()
      .then((resp) =>
        dispatch({
          type: customerTypes.GET_TENANCY_SUCCESS,
          payload: resp,
        })
      )
      .catch((err) => {
        dispatch({
          type: customerTypes.GET_TENANCY_ERROR,
          payload: err,
        });
      })
      .then(() => (action ? dispatch(action) : null))
      .then(() => done());
  }
};

const getAccount = createLogic({
  type: customerTypes.GET_ACCOUNT,
  cancelType: customerTypes.GET_ACCOUNT_ERROR,
  process({ getState, action }, dispatch, done) {
    const { error, roles } = getState().auth;
    const { loaded } = getState().customer.tenancy;
    if (loaded == constants.UNLOADED) {
      getTenancy(error, dispatch, done, customerActions.getAccount());
    } else if (
      loaded == constants.LOADED &&
      hasRole(roles, constants.ROLE_ACCOUNT_READ)
    ) {
      const { currentTenancy } = getState().customer.tenancy.data;
      const tenancyRef = get(currentTenancy, 'tenancyRef', null);

      customerApi
        .getAccount(tenancyRef)
        .then((resp) =>
          dispatch({
            type: customerTypes.GET_ACCOUNT_SUCCESS,
            payload: resp,
          })
        )
        .catch((err) => {
          dispatch({
            type: customerTypes.GET_ACCOUNT_ERROR,
            payload: err,
          });
        })
        .then(() => done());
    } else {
      done();
    }
  },
});

const getPreferences = createLogic({
  type: customerTypes.GET_PREFERENCES,
  cancelType: customerTypes.GET_PREFERENCES_ERROR,
  process({ getState, action }, dispatch, done) {
    const { error, roles } = getState().auth;
    preferencesApi
      .getPreferences()
      .then((resp) =>
        dispatch({
          type: customerTypes.GET_PREFERENCES_SUCCESS,
          payload: resp,
        })
      )
      .catch((err) => {
        dispatch({
          type: customerTypes.GET_PREFERENCES_ERROR,
          payload: err,
        });
      })
      .then(() => done());
  },
});

const createPreferences = createLogic({
  ...generateBaseConfig(customerTypes.CREATE_PREFERENCES),
  process({ getState }) {
    const { data, submittedNotificationPreference } =
      getState().customer.preferences;
    return preferencesApi.createPreferences(
      submittedNotificationPreference.length
        ? { preferences: submittedNotificationPreference }
        : data
    );
  },
});

const getLetters = createLogic({
  type: customerTypes.GET_LETTERS,
  cancelType: customerTypes.GET_LETTERS_ERROR,
  process({ getState, action }, dispatch, done) {
    const { error, roles } = getState().auth;
    const { loaded } = getState().customer.tenancy;
    if (loaded == constants.UNLOADED) {
      getTenancy(error, dispatch, done, customerActions.getLetters());
    } else if (
      loaded == constants.LOADED &&
      hasRole(roles, constants.ROLE_APPOINTMENTS_READ)
    ) {
      const { currentTenancy } = getState().customer.tenancy.data;
      customerApi
        .getLetters(currentTenancy.tenancyRef)
        .then((resp) =>
          dispatch({
            type: customerTypes.GET_LETTERS_SUCCESS,
            payload: resp,
          })
        )
        .catch((err) => {
          dispatch({
            type: customerTypes.GET_LETTERS_ERROR,
            payload: err,
          });
        })
        .then(() => done());
    } else {
      done();
    }
  },
});

const getTransactions = createLogic({
  type: customerTypes.GET_TRANSACTIONS,
  cancelType: customerTypes.GET_TRANSACTIONS_ERROR,
  process({ getState, action }, dispatch, done) {
    const { error } = getState().auth;
    const { loaded } = getState().customer.account;
    const { accountType } = getState().app;

    if (!error) {
      if (loaded == constants.UNLOADED) {
        customerApi
          .getTenancy()
          .then((resp) =>
            dispatch({
              type: customerTypes.GET_TENANCY_SUCCESS,
              payload: resp,
            })
          )
          .catch((err) => {
            dispatch({
              type: customerTypes.GET_TENANCY_ERROR,
              payload: err,
            });
          })
          .then((resp) => {
            const { currentTenancy } = getState().customer.tenancy.data;
            return customerApi
              .getAccount(currentTenancy.tenancyRef)
              .then((resp) =>
                dispatch({
                  type: customerTypes.GET_ACCOUNT_SUCCESS,
                  payload: resp,
                })
              );
          })
          .then((resp) => {
            const { currentTenancy } = getState().customer.tenancy.data;
            const { paymentReference } =
              getState().customer.account.data.accounts[accountType];
            return customerApi
              .getTransactions(currentTenancy.tenancyRef, paymentReference)
              .then((resp) =>
                dispatch({
                  type: customerTypes.GET_TRANSACTIONS_SUCCESS,
                  payload: { resp, accountType },
                })
              )
              .catch((err) => {
                dispatch({
                  type: customerTypes.GET_TRANSACTIONS_ERROR,
                  payload: { err, accountType },
                });
              });
          })
          .then(() => dispatch({ type: customerTypes.GET_APPOINTMENTS }))
          .then(() => done());
      } else {
        const { currentTenancy } = getState().customer.tenancy.data;
        const { paymentReference } =
          getState().customer.account.data.accounts[accountType];
        customerApi
          .getTransactions(currentTenancy.tenancyRef, paymentReference)
          .then((resp) =>
            dispatch({
              type: customerTypes.GET_TRANSACTIONS_SUCCESS,
              payload: { resp, accountType },
            })
          )
          .catch((err) => {
            dispatch({
              type: customerTypes.GET_TRANSACTIONS_ERROR,
              payload: { err, accountType },
            });
          })
          .then(() => done());
      }
    } else {
      dispatch({
        type: customerTypes.GET_TENANCY_ERROR,
        payload: { fatal: true },
      });
      done();
    }
  },
});

const getWorkRequests = createLogic({
  type: customerTypes.GET_WORK_REQUESTS,
  cancelType: customerTypes.GET_WORK_REQUESTS_ERROR,
  process({ getState, action }, dispatch, done) {
    const { error } = getState().auth;
    const { loaded } = getState().customer.tenancy;
    if (loaded == constants.UNLOADED) {
      const nextAction = customerActions.getWorkRequests(action.payload);

      getTenancy(error, dispatch, done, nextAction);
    } else if (loaded == constants.LOADED) {
      const { propertyReference } =
        getState().customer.tenancy.data.currentTenancy;
      customerApi
        .getWorkRequests(propertyReference, action.payload)
        .then((resp) =>
          dispatch({
            type: customerTypes.GET_WORK_REQUESTS_SUCCESS,
            payload: { ...resp, status: action.payload },
          })
        )
        .catch((err) => {
          dispatch({
            type: customerTypes.GET_WORK_REQUESTS_ERROR,
            payload: { ...err, status: action.payload },
          });
        })
        .then(() => done());
    } else {
      done();
    }
  },
});

const createArrangement = createLogic({
  ...generateBaseConfig(customerTypes.CREATE_ARRANGEMENT),
  process({ getState }) {
    const { createArrangement } = getState().customer;
    const { tenancyRef } = getState().customer.tenancy.data.currentTenancy;
    return customerApi.createArrangement(createArrangement, tenancyRef);
  },
});

const getAppointments = createLogic({
  type: customerTypes.GET_APPOINTMENTS,
  cancelType: customerTypes.GET_APPOINTMENTS_ERROR,
  process({ getState, action }, dispatch, done) {
    const { error, roles } = getState().auth;
    const { loaded } = getState().customer.tenancy;
    if (loaded == constants.UNLOADED) {
      getTenancy(error, dispatch, done);
    } else if (
      loaded == constants.LOADED &&
      hasRole(roles, constants.ROLE_APPOINTMENTS_READ)
    ) {
      const { currentTenancy } = getState().customer.tenancy.data;
      customerApi
        .getAppointments(currentTenancy.tenancyRef)
        .then((resp) =>
          dispatch({
            type: customerTypes.GET_APPOINTMENTS_SUCCESS,
            payload: resp,
          })
        )
        .catch((err) => {
          dispatch({
            type: customerTypes.GET_APPOINTMENTS_ERROR,
            payload: err,
          });
        })
        .then(() => done());
    } else {
      done();
    }
  },
});

const getContactDetails = createLogic({
  ...generateBaseConfig(customerTypes.GET_CONTACT_DETAILS),
  process({ getState, action }) {
    return customerApi.getContactDetails();
  },
});

const getBankDetails = createLogic({
  ...generateBaseConfig(customerTypes.GET_BANK_DETAILS),
  process({ getState, action }) {
    return customerApi.getBankDetails();
  },
});

const deleteContact = createLogic({
  type: customerTypes.DELETE_CONTACT,
  cancelType: customerTypes.DELETE_CONTACT_ERROR,
  process({ getState, action }, dispatch, done) {
    const { error } = getState().auth;
    const { contactToDelete } = getState().app;
    const currentOutage = getCurrentOutage(outages);
    if (!cmsAccess && currentOutage && inOutage(currentOutage)) {
      dispatch(appActions.setAppOutage(currentOutage));
    } else if (!error) {
      customerApi
        .deleteContact(contactToDelete)
        .then((resp) =>
          dispatch({
            type: customerTypes.DELETE_CONTACT_SUCCESS,
            payload: resp,
          })
        )
        .catch((err) => {
          dispatch({
            type: customerTypes.DELETE_CONTACT_ERROR,
            payload: err,
          });
        })
        .then(() =>
          dispatch({
            type: appTypes.SET_CONFIRMED_OPEN,
          })
        )
        .then(() => done());
    } else {
      dispatch({
        type: customerTypes.DELETE_CONTACT_ERROR,
        payload: { fatal: true },
      });
      done();
    }
  },
});

const updateContact = createLogic({
  type: customerTypes.UPDATE_CONTACT,
  cancelType: customerTypes.UPDATE_CONTACT_ERROR,
  process({ getState, action }, dispatch, done) {
    const { error } = getState().auth;
    const { updateContact, updateContactId } = getState().customer;
    const currentOutage = getCurrentOutage(outages);
    if (!cmsAccess && currentOutage && inOutage(currentOutage)) {
      dispatch(appActions.setAppOutage(currentOutage));
    } else if (!error) {
      customerApi
        .updateContact(updateContactId, updateContact)
        .then((resp) =>
          dispatch({
            type: customerTypes.UPDATE_CONTACT_SUCCESS,
            payload: resp,
          })
        )
        .catch((err) => {
          dispatch({
            type: customerTypes.UPDATE_CONTACT_ERROR,
            payload: err,
          });
        })
        .then(() =>
          dispatch({
            type: appTypes.SET_CONFIRMED_OPEN,
          })
        )
        .then(() => done());
    } else {
      dispatch({
        type: customerTypes.UPDATE_CONTACT_ERROR,
        payload: { fatal: true },
      });
      done();
    }
  },
});

const deleteBank = createLogic({
  type: customerTypes.DELETE_BANK,
  cancelType: customerTypes.DELETE_BANK_ERROR,
  process({ getState, action }, dispatch, done) {
    const { error } = getState().auth;
    const { deleteAccountId } = getState().customer;
    const currentOutage = getCurrentOutage(outages);
    if (!cmsAccess && currentOutage && inOutage(currentOutage)) {
      dispatch(appActions.setAppOutage(currentOutage));
    } else if (!error) {
      customerApi
        .deleteBankDetails(deleteAccountId)
        .then((resp) =>
          dispatch({
            type: customerTypes.DELETE_BANK_SUCCESS,
            payload: resp,
          })
        )
        .catch((err) => {
          dispatch({
            type: customerTypes.DELETE_BANK_ERROR,
            payload: err,
          });
        })
        .then(() =>
          dispatch({
            type: appTypes.SET_CONFIRMED_OPEN,
          })
        )
        .then(() => done());
    } else {
      dispatch({
        type: customerTypes.DELETE_BANK_ERROR,
        payload: { fatal: true },
      });
      done();
    }
  },
});

const updateBank = createLogic({
  type: customerTypes.UPDATE_BANK,
  cancelType: customerTypes.UPDATE_BANK_ERROR,
  process({ getState, action }, dispatch, done) {
    const { error } = getState().auth;
    const { updateBankAccount, updateAccountId } = getState().customer;
    const currentOutage = getCurrentOutage(outages);
    if (!cmsAccess && currentOutage && inOutage(currentOutage)) {
      dispatch(appActions.setAppOutage(currentOutage));
    } else if (!error) {
      customerApi
        .updateBankDetails(updateAccountId, updateBankAccount)
        .then((resp) =>
          dispatch({
            type: customerTypes.UPDATE_BANK_SUCCESS,
            payload: resp,
          })
        )
        .catch((err) => {
          dispatch({
            type: customerTypes.UPDATE_BANK_ERROR,
            payload: err,
          });
        })
        .then(() =>
          dispatch({
            type: appTypes.SET_CONFIRMED_OPEN,
          })
        )
        .then(() => done());
    } else {
      dispatch({
        type: customerTypes.UPDATE_BANK_ERROR,
        payload: { fatal: true },
      });
      done();
    }
  },
});

const createBank = createLogic({
  type: customerTypes.CREATE_BANK,
  cancelType: customerTypes.CREATE_BANK_ERROR,
  process({ getState, action }, dispatch, done) {
    const { error } = getState().auth;
    const { createBankAccount } = getState().customer;
    const currentOutage = getCurrentOutage(outages);
    if (!cmsAccess && currentOutage && inOutage(currentOutage)) {
      dispatch(appActions.setAppOutage(currentOutage));
    } else if (!error) {
      customerApi
        .createBankDetails(createBankAccount)
        .then((resp) =>
          dispatch({
            type: customerTypes.CREATE_BANK_SUCCESS,
            payload: resp,
          })
        )
        .catch((err) => {
          dispatch({
            type: customerTypes.CREATE_BANK_ERROR,
            payload: err,
          });
        })
        .then(() =>
          dispatch({
            type: appTypes.SET_CONFIRMED_OPEN,
          })
        )
        .then(() => done());
    } else {
      dispatch({
        type: customerTypes.CREATE_BANK_ERROR,
        payload: { fatal: true },
      });
      done();
    }
  },
});

const createConsentedPerson = createLogic({
  type: customerTypes.CREATE_CONSENTED_PERSON,
  cancelType: customerTypes.CREATE_CONSENTED_PERSON_ERROR,
  process({ getState, action }, dispatch, done) {
    const { error } = getState().auth;
    const { createConsentedPerson } = getState().customer;
    const currentOutage = getCurrentOutage(outages);
    if (!cmsAccess && currentOutage && inOutage(currentOutage)) {
      dispatch(appActions.setAppOutage(currentOutage));
    } else if (!error) {
      customerApi
        .createConsentedPerson(createConsentedPerson)
        .then((resp) =>
          dispatch({
            type: customerTypes.CREATE_CONSENTED_PERSON_SUCCESS,
            payload: resp,
          })
        )
        .catch((err) => {
          dispatch({
            type: customerTypes.CREATE_CONSENTED_PERSON_ERROR,
            payload: err,
          });
        })
        .then(() =>
          dispatch({
            type: appTypes.SET_CONFIRMED_OPEN,
          })
        )
        .then(() => done());
    } else {
      dispatch({
        type: customerTypes.CREATE_CONSENTED_PERSON_ERROR,
        payload: { fatal: true },
      });
      done();
    }
  },
});

const deleteConsentedPerson = createLogic({
  type: customerTypes.DELETE_CONSENTED_PERSON,
  cancelType: customerTypes.DELETE_CONSENTED_PERSON_ERROR,
  process({ getState, action }, dispatch, done) {
    const { error } = getState().auth;
    const { deleteConsentedPersonId } = getState().customer;
    const currentOutage = getCurrentOutage(outages);
    if (!cmsAccess && currentOutage && inOutage(currentOutage)) {
      dispatch(appActions.setAppOutage(currentOutage));
    } else if (!error) {
      customerApi
        .deleteConsentedPerson(deleteConsentedPersonId)
        .then((resp) =>
          dispatch({
            type: customerTypes.DELETE_CONSENTED_PERSON_SUCCESS,
            payload: resp,
          })
        )
        .catch((err) => {
          dispatch({
            type: customerTypes.DELETE_CONSENTED_PERSON_ERROR,
            payload: err,
          });
        })
        .then(() =>
          dispatch({
            type: appTypes.SET_CONFIRMED_OPEN,
          })
        )
        .then(() => done());
    } else {
      dispatch({
        type: customerTypes.DELETE_CONSENTED_PERSON_ERROR,
        payload: { fatal: true },
      });
      done();
    }
  },
});

export default [
  getAccount,
  getLetters,
  getTransactions,
  getWorkRequests,
  createArrangement,
  getAppointments,
  getContactDetails,
  getBankDetails,
  deleteContact,
  updateContact,
  deleteBank,
  updateBank,
  createBank,
  getPreferences,
  createPreferences,
  createConsentedPerson,
  deleteConsentedPerson,
];
